import { useEffect, useState } from "react";
import { Input, Spinner, Text, XStack, YStack } from "tamagui";
import { matchByPhone } from "@screen/Onboarding/registerApi";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { maskPhone } from "@utils/func/maskPhone";
import { maskHouseNo, normalizeHouseNo, isValidHouseNo } from "@utils/func/houseNo";
import { kiosk } from "../kioskTheme";
import { useKiosk } from "../useKiosk";
import { Card, SectionLabel } from "./ConfirmParts";

export type IdentityResult =
  /** houseNo＝客戶在確認卡輸入的門牌號（已正規化），供 walk-in 送後端 verifyClaimedCis 驗證；未開啟驗證時為 undefined。 */
  { type: "existing"; cisId?: string; houseNo?: string } | { type: "new" };

interface Props {
  address: string;
  phone: string;
  /** 真實 supplier_id（chooseIdentity.primary_supplier 或 onboarding shopCode 解析出）；沒有就無法呼叫真後端比對。 */
  supplierId?: string;
  /** 入口自述是既有客戶（家用電話路徑）→ 相似度比對偏向找到；純新客戶 → 直接視為新客戶。 */
  assumeMatched: boolean;
  onResolved: (r: IdentityResult) => void;
}

const Btn = ({ solid, disabled, onPress, children }: { solid?: boolean; disabled?: boolean; onPress: () => void; children: React.ReactNode }) => (
  <YStack flex={1} ai="center" jc="center" height={46} borderRadius={12} cursor={disabled ? "default" : "pointer"} onPress={disabled ? undefined : onPress} backgroundColor={disabled ? "#E5E7EB" : solid ? kiosk.blue : "#fff"} borderWidth={solid || disabled ? 0 : 1.5} borderColor={kiosk.line} pressStyle={disabled ? undefined : { opacity: 0.9 }}>
    <Text fontSize={14} fontWeight="800" color={disabled ? "#999" : solid ? "#fff" : kiosk.ink}>{children}</Text>
  </YStack>
);

/**
 * 結帳頁「就地身分核對」：有了配送地址後再做一次既有客戶比對。
 * 比對到 → 顯示客戶資料讓客戶確認（這是您嗎）；比對不到/不是我 → 視為新客戶（鋼瓶/用途另卡收）。
 * Phase 1：以手機精確比對（POST /register/match/by-phone）取代原本前端模擬 timeout；
 * 地址相似度比對屬 Phase 2，尚未串接。
 */
const IdentityCheckCard = ({ address, phone, supplierId, assumeMatched, onResolved }: Props) => {
  const { lineAccessToken } = useLineAuth();
  const { phoneVerifiedTicket, setMatchedCisId } = useKiosk();
  const [phase, setPhase] = useState<"matching" | "confirm">("matching");
  const [custAddr, setCustAddr] = useState(address);
  const [cisId, setCisId] = useState<string | undefined>(undefined);
  const [houseNo, setHouseNo] = useState(""); // 確認卡上客戶輸入的門牌號（身分驗證用）
  // 門牌號驗證：地址遮掉門牌號、改由客戶輸入比對。遮罩與輸入框不可只留其一（只遮不問只會更難辨識）。
  const houseNoOk = isValidHouseNo(houseNo);

  useEffect(() => {
    // 入口未自述是既有客戶 → 不需要比對，直接視為新客戶（維持原行為）
    if (!assumeMatched) {
      onResolved({ type: "new" });
      return;
    }
    // 沒有真實 supplier_id / 沒有門號 → 無法呼叫真後端比對，視為新客戶（見 docs/impl-notes-fe-walkin.md）
    if (!supplierId || !phone) {
      onResolved({ type: "new" });
      return;
    }
    let cancelled = false;
    matchByPhone({ accessToken: lineAccessToken || "", supplier_id: supplierId, phone, phone_verified_ticket: phoneVerifiedTicket || undefined })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (data?.matched && data.customer?.address_incomplete) {
          // 帶入地址不完整（沒地址／沒門牌號／拆不出路名）→ 不出確認卡，直接視為新客戶
          //（2026-08-12：這種資料無從以門牌號核對，出卡只會走進 HOUSE_NO_UNVERIFIABLE 死路）
          onResolved({ type: "new" });
        } else if (data?.matched) {
          // 隱私：確認身分不顯示系統帶出的姓名，改以遮罩手機 + 遮罩地址讓客戶確認。
          setCustAddr(data.customer?.address_masked || address);
          setCisId(data.customer?.cis_id);
          setPhase("confirm");
        } else {
          onResolved({ type: "new" });
        }
      })
      .catch(() => {
        if (!cancelled) onResolved({ type: "new" }); // 比對失敗 → 不擋流程，視為新客戶
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [assumeMatched, supplierId, phone]);

  const confirmExisting = () => {
    if (!houseNoOk) return;
    if (cisId) setMatchedCisId(cisId); // 供後續 walk-in 綁定既有 CIS（後端仍會重驗）
    onResolved({ type: "existing", cisId, houseNo: normalizeHouseNo(houseNo) });
  };

  return (
    <>
      <SectionLabel>身分確認</SectionLabel>
      <Card>
        {phase === "matching" ? (
          <XStack ai="center" gap={10} py={10}>
            <Spinner size="small" color={kiosk.blue} />
            <Text fontSize={13.5} color={kiosk.grey}>正在依門號為您比對既有客戶資料…</Text>
          </XStack>
        ) : (
          <YStack py={4} gap={14}>
            <YStack gap={6} p={14} borderRadius={14} backgroundColor="#F0FDF4" borderWidth={1} borderColor="#BBF7D0">
              <Text fontSize={13} fontWeight="800" color="#15803D">找到您的既有資料，這是您嗎？</Text>
              <Text fontSize={13} color={kiosk.ink} lineHeight={20}>
                {maskPhone(phone) || "既有門號"}
                {"\n"}
                {/* 地址一律再遮一次：答案不能印在要客戶回答的卡片上 */}
                {maskHouseNo(custAddr)}
              </Text>
            </YStack>
            {/* 門牌號輸入放在確認卡「外面」：卡片是系統給的資料，這裡是要客戶回答的問題，兩者不混在一起。 */}
            <YStack gap={8}>
              <Text fontSize={13} fontWeight="700" color={kiosk.grey}>請輸入上述地址的門牌號碼</Text>
              <XStack ai="center" gap={10}>
                <Input
                  flex={1}
                  height={46}
                  value={houseNo}
                  onChangeText={setHouseNo}
                  inputMode="numeric"
                  placeholder="例：20"
                  aria-label="門牌號碼"
                  backgroundColor="#F1F5F9"
                  borderWidth={0}
                  borderRadius={12}
                  fontSize={16}
                />
                <Text fontSize={14} fontWeight="800" color={kiosk.ink}>號</Text>
              </XStack>
              {!!houseNo && !isValidHouseNo(houseNo) && (
                <Text fontSize={12} fontWeight="700" color="#EF4444">請只填門牌號碼，例：20 或 20-1。</Text>
              )}
            </YStack>
            <XStack gap={10}>
              <Btn onPress={() => onResolved({ type: "new" })}>不是我</Btn>
              <Btn solid disabled={!houseNoOk} onPress={confirmExisting}>是我，帶入資料</Btn>
            </XStack>
          </YStack>
        )}
      </Card>
    </>
  );
};

export default IdentityCheckCard;
